/**
 * ToolPageHeader — per-tool workspace header (X.3.1 → X.6.4B design refresh).
 *
 * Back control + a chip (forest dove) + Playfair title + a one-line description.
 * The chip tints terracotta (pepper) when `spice` is set — driven purely by
 * the boolean the page passes, so any tool (including Brand Formatter once it
 * gains a SPICE mode) lights up with no change here. x.6.1 tokens; zero hexes.
 */

import { type ReactNode } from 'react';
import { BackControl } from '@/components/BackControl';
import { HubBrandMark } from '@/components/hub/HubBrandMark';
import type { AiToolMeta, AiToolSlug } from '@/pages/AiTools';

/**
 * Workspace-header descriptions (distinct from the landing cards, which carry
 * none per the X.6.4 decision). Role/tenant-neutral, no staff names. A
 * tool whose tile already says what it does (email, assessment, assessment
 * letter, intake) has no entry and shows its title alone.
 */
const TOOL_DESCRIPTIONS: Partial<Record<AiToolSlug, string>> = {
  'brand-formatter':
    'Format pasted text into a branded Peace document. The wording stays yours; nothing is invented.',
  'med-list':
    'Build a medication list in the company template, from a client record or a paste. Nothing is invented; you review every row.',
};

export interface ToolPageHeaderProps {
  tool: AiToolMeta;
  tenantSlug: string;
  /** SPICE mode tints the chip pepper (X.6.4B). Generic across tools. */
  spice?: boolean;
}

export function ToolPageHeader({ tool, tenantSlug, spice = false }: ToolPageHeaderProps): ReactNode {
  return (
    <header>
      <BackControl
        label="Writing"
        ariaLabel="Back to writing"
        to={`/t/${encodeURIComponent(tenantSlug)}/ai-tools`}
        testId="back-to-writing"
      />
      <div className="mt-4 flex items-center gap-[15px]">
        <span
          data-testid="tool-header-chip"
          className={
            'flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-card shadow-sh1 transition-colors duration-[var(--dur-base)] ease-quiet ' +
            (spice ? 'bg-pepper' : 'bg-sage-800')
          }
        >
          <HubBrandMark
            label=""
            className="flex w-7 [&>svg]:h-auto [&>svg]:w-full"
            style={{ color: 'var(--color-cream)' }}
          />
        </span>
        <div>
          <h1 className="font-display text-[clamp(26px,3vw,36px)] font-semibold tracking-[-0.01em] text-sage-800">
            {tool.title}
          </h1>
          {TOOL_DESCRIPTIONS[tool.slug] && (
            <p className="mt-1 max-w-[520px] text-[14.5px] text-ink-soft">
              {TOOL_DESCRIPTIONS[tool.slug]}
            </p>
          )}
        </div>
      </div>
    </header>
  );
}
